<template>
  <div id="app" class="app-content mb-4">
    <app-header></app-header>
    <div class="container">
      <div class="row">
        <div class="col-lg-12 text-center">
          <div class="amej-title"><h1>Withdraw Fund</h1></div>
        </div>
      </div>

      <div class="row">
        <!-- ============ REQUEST FORM ============ -->
        <div class="col-lg-8 mb-3">
          <div class="wd-card">
            <div class="balance-line">
              <span>Available Balance</span>
              <strong>$ {{ money(balanceCents) }}</strong>
            </div>

            <div v-if="!myBanks.length" class="empty-box">
              You have no bank account saved. Please add one from
              <router-link :to="{ name: 'Bank' }">Bank settings</router-link>
              before withdrawing.
            </div>

            <b-form v-else @submit.stop.prevent="startWithdraw">
              <div class="form-row-x">
                <label>Withdraw To *</label>
                <b-form-select v-model="form.bank" :options="bankOptions"></b-form-select>
                <span class="text-danger" v-if="errors.bank">{{ errors.bank }}</span>
              </div>
              <div class="form-row-x">
                <label>Amount (USD) *</label>
                <b-form-input
                  v-model="form.amount" placeholder="0.00"
                  oninput="this.value = this.value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');"
                ></b-form-input>
                <span class="text-danger" v-if="errors.amount">{{ errors.amount }}</span>
              </div>
              <div class="form-row-x">
                <label>Remarks</label>
                <b-form-input v-model.trim="form.remarks" placeholder="Optional note"></b-form-input>
              </div>
              <div class="text-danger mb-2" v-if="formError">{{ formError }}</div>
              <b-button variant="primary" type="submit" :disabled="isSending">
                <b-spinner small v-if="isSending" class="mr-2"></b-spinner>
                {{ isSending ? "Sending code..." : "Request Withdraw" }}
              </b-button>
              <p class="wd-hint">
                Charges are worked out by the bank once you submit. You will see the exact
                amount that reaches your account before you confirm.
              </p>
            </b-form>
          </div>
        </div>

        <!-- ============ SIDEBAR ============ -->
        <!-- The limits used to sit on top of the form and pushed it below the fold.
             They are reference material, not input, so they belong beside it. -->
        <div class="col-lg-4 mb-3">
          <div class="wd-side">
            <div class="wd-limits" v-if="hasLimits">
              <div class="wd-limits-head">
                <span class="material-icons">info</span>
                <span>Withdrawal limits</span>
                <span class="wd-tier" v-if="limitTier">{{ limitTier }}</span>
              </div>
              <div class="wd-limit-grid">
                <div class="wd-limit" v-for="row in limitRows" :key="row.label">
                  <span class="wd-limit-label">{{ row.label }}</span>
                  <span class="wd-limit-value">{{ row.value }}</span>
                  <small class="wd-limit-note" v-if="row.note">{{ row.note }}</small>
                </div>
              </div>
              <div class="wd-limit-warn" v-if="dailyCountExhausted">
                You have used all of today's withdrawal requests. The next one can be made tomorrow.
              </div>
              <router-link class="wd-verify" :to="{ name: 'Kyc' }" v-if="!isKycVerified">
                Verify your identity to raise these limits
                <i class="material-icons">arrow_right_alt</i>
              </router-link>
            </div>

            <div class="wd-info">
              <div class="wd-info-head">
                <span class="material-icons">receipt_long</span>
                <span>How charges are applied</span>
              </div>
              <ol class="wd-info-list">
                <li>A withdrawal fee is taken from the amount you request.</li>
                <li>If the money is paid out in another currency, a conversion fee is taken as well.</li>
                <li>What is left after both is what reaches your bank account.</li>
              </ol>
              <p class="wd-info-foot">
                The full breakdown is shown before you confirm, and again on every entry
                in your history.
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- ============ HISTORY ============ -->
      <div class="row mt-2">
        <div class="col-12">
          <h5 class="list-title">Withdrawal History</h5>
        </div>
      </div>

      <div class="row wd-filters align-items-end">
        <div class="col-6 col-md-3 col-lg-2 mb-2">
          <label>Status</label>
          <b-form-select v-model="filters.status" :options="statusOptions"></b-form-select>
        </div>
        <div class="col-6 col-md-3 col-lg-2 mb-2">
          <label>Bank</label>
          <b-form-select v-model="filters.bank" :options="bankFilterOptions"></b-form-select>
        </div>
        <div class="col-6 col-md-3 col-lg-2 mb-2">
          <label>Min amount</label>
          <b-form-input v-model="filters.minAmount" type="number" min="0" step="0.01" placeholder="0.00"></b-form-input>
        </div>
        <div class="col-6 col-md-3 col-lg-2 mb-2">
          <label>Max amount</label>
          <b-form-input v-model="filters.maxAmount" type="number" min="0" step="0.01" placeholder="Any"></b-form-input>
        </div>
        <div class="col-12 col-md-6 col-lg-3 mb-2">
          <label>Search</label>
          <b-form-input v-model="filters.search" type="search"
            placeholder="reference, bank or remarks"></b-form-input>
        </div>
        <div class="col-12 col-md-6 col-lg-1 mb-2">
          <b-button variant="light" block @click="resetFilters">Reset</b-button>
        </div>
      </div>

      <div class="row">
        <div class="col-12">
          <div class="wd-count" v-if="totalItems">
            Showing {{ firstIndex }}-{{ lastIndex }} of {{ totalItems }}
            <span v-if="totalItems !== withdraws.length">
              (filtered from {{ withdraws.length }})
            </span>
            &middot; newest first
          </div>

          <b-table
            hover responsive striped bordered outlined show-empty
            class="wd-table"
            :items="pagedWithdraws" :fields="fields" :busy="isLoading"
          >
            <template #table-busy>
              <div class="text-center py-4"><b-spinner label="Loading..."></b-spinner></div>
            </template>
            <template #empty>
              <div class="text-center py-3">
                {{ withdraws.length ? "No withdrawals match these filters." : "No withdrawals yet." }}
              </div>
            </template>
            <template #cell(index)="data">{{ firstIndex + data.index }}.</template>
            <template #cell(bank)="data">
              <span class="wd-bank">{{ bankLabel(data.item.bank) }}</span>
              <small class="wd-ref" v-if="data.item.id">Ref #{{ data.item.id }}</small>
            </template>
            <template #cell(amount)="data">$ {{ money(data.item.amount) }}</template>
            <template #cell(total_fee)="data">
              <span :class="num(data.item.total_fee) > 0 ? 'debit' : ''">
                {{ has(data.item.total_fee) ? "- $ " + money(data.item.total_fee) : "-" }}
              </span>
            </template>
            <template #cell(net_amount)="data">
              <strong>{{ has(data.item.net_amount) ? "$ " + money(data.item.net_amount) : "-" }}</strong>
            </template>
            <template #cell(payout)="data">
              <span v-if="hasPayout(data.item)">
                {{ data.item.payout_currency }} {{ plain(data.item.payout_amount) }}
              </span>
              <span v-else class="muted">-</span>
            </template>
            <template #cell(status)="data">
              <span class="status-pill" :class="statusClass(data.item.status)">
                {{ prettify(data.item.status) }}
              </span>
            </template>
            <template #cell(actions)="data">
              <a href="#" class="wd-view" @click.prevent="openDetail(data.item)">View</a>
            </template>
          </b-table>

          <div class="st-pager" v-if="totalItems">
            <div class="st-pagesize">
              <span>Rows per page</span>
              <b-form-select v-model="perPage" :options="pageSizeOptions" size="sm"></b-form-select>
            </div>
            <nav aria-label="Withdrawal pagination">
              <ul class="pagination mb-0">
                <li class="page-item" :class="safePage <= 1 ? 'disabled' : ''">
                  <a href="#" class="page-link" @click.prevent="goToPage(safePage - 1)">&laquo; Previous</a>
                </li>
                <li class="page-item" v-for="index in pageWindow" :key="index"
                    :class="safePage === index ? 'active' : ''">
                  <a href="#" class="page-link" @click.prevent="goToPage(index)">{{ index }}</a>
                </li>
                <li class="page-item" :class="safePage >= totalPages ? 'disabled' : ''">
                  <a href="#" class="page-link" @click.prevent="goToPage(safePage + 1)">Next &raquo;</a>
                </li>
              </ul>
            </nav>
            <div class="st-pageinfo">Page {{ safePage }} of {{ totalPages }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- ============ DETAIL ============ -->
    <b-modal id="wd-detail-modal" centered size="lg" title="Withdrawal Detail">
      <div class="txn-detail">
        <div class="txn-head">
          <div class="txn-amount">
            {{ has(detail.net_amount) ? "$ " + money(detail.net_amount) : "$ " + money(detail.amount) }}
          </div>
          <span class="status-pill" :class="statusClass(detail.status)">
            {{ prettify(detail.status) }}
          </span>
          <div class="txn-sub" v-if="has(detail.net_amount)">
            Amount reaching your bank account
          </div>
        </div>

        <div class="txn-section" v-for="(section, si) in detailSections" :key="'s' + si">
          <h6 class="txn-section-title">{{ section.title }}</h6>
          <div class="txn-row" v-for="(f, i) in section.items" :key="'r' + si + '-' + i"
               :class="f.strong ? 'is-strong' : ''">
            <span class="txn-key">{{ f.label }}</span>
            <span class="txn-val">{{ f.value || '-' }}</span>
          </div>
        </div>
      </div>

      <template #modal-footer="{ cancel }">
        <b-button size="sm" variant="secondary" @click="cancel()">Close</b-button>
      </template>
    </b-modal>

    <!-- ============ OTP + PASSWORD ============ -->
    <b-modal id="wd-otp-modal" centered title="Confirm Withdrawal" :no-close-on-backdrop="true">
      <!-- Only the fields the server actually returned are listed, so an older
           response body degrades to the plain amount instead of "$ 0.00" charges. -->
      <div class="wd-breakdown" v-if="draftRows.length">
        <div class="wd-breakdown-row" v-for="(r, i) in draftRows" :key="'d' + i"
             :class="r.strong ? 'is-strong' : ''">
          <span>{{ r.label }}</span>
          <span>{{ r.value }}</span>
        </div>
      </div>
      <p class="note">
        Enter the code sent to your email and your account password to confirm this withdrawal.
      </p>
      <div class="form-row-x">
        <label>Verification Code *</label>
        <b-form-input
          v-model.trim="otp" maxlength="6" placeholder="6-digit code"
          onkeypress="return event.charCode >= 48 && event.charCode <= 57"
        ></b-form-input>
        <span class="text-danger" v-if="errors.otp">{{ errors.otp }}</span>
      </div>
      <div class="form-row-x">
        <label>Account Password *</label>
        <b-form-input v-model="password" type="password" placeholder="Your login password"></b-form-input>
        <span class="text-danger" v-if="errors.password">{{ errors.password }}</span>
      </div>
      <div class="text-danger" v-if="payError">{{ payError }}</div>
      <div class="mt-2">
        <a href="#" class="link-muted" v-if="resendCooldown === 0" @click.prevent="resendCode">Resend code</a>
        <span class="muted" v-else>Resend code in {{ resendCooldown }}s</span>
      </div>
      <template #modal-footer="{ cancel }">
        <b-button size="sm" variant="success" :disabled="isConfirming" @click="confirmWithdraw">
          <b-spinner small v-if="isConfirming" class="mr-1"></b-spinner>
          {{ isConfirming ? "Processing..." : "Confirm Withdraw" }}
        </b-button>
        <b-button size="sm" variant="secondary" @click="cancel()">Cancel</b-button>
      </template>
    </b-modal>

    <b-toast id="wd-toast" variant="success" solid append-toast toaster="b-toaster-top-center">
      <div slot="toast-title" class="d-flex flex-grow-1 align-items-baseline">
        <strong class="mr-auto">Amej Pay</strong>
      </div>
      <div><p>{{ infoText }}</p></div>
    </b-toast>
    <b-toast id="wd-error-toast" variant="danger" solid append-toast toaster="b-toaster-top-center">
      <div slot="toast-title" class="d-flex flex-grow-1 align-items-baseline">
        <strong class="mr-auto">Withdrawal Failed</strong>
      </div>
      <div><p>{{ payError }}</p></div>
    </b-toast>
  </div>
</template>

<script>
import AppHeader from "../reuseable_components/Header.vue";
import { mapActions, mapState } from "vuex";
import { centsToAmount, amountToCents, cleanText } from "@/utils/format";

/** Every status the API has been seen to return, in the order they occur. */
const KNOWN_STATUSES = [
  "requested", "on_process", "processing", "approved",
  "completed", "success", "rejected", "failed", "cancelled",
];

export default {
  name: "WithdrawFund",
  components: { "app-header": AppHeader },
  data() {
    return {
      isLoading: false,
      isSending: false,
      isConfirming: false,
      form: { bank: null, amount: "", remarks: "" },
      errors: {},
      formError: "",
      payError: "",
      infoText: "",
      otp: "",
      password: "",
      draft: {},
      detail: {},
      resendCooldown: 0,
      cooldownTimer: null,
      currentPage: 1,
      perPage: 10,
      pageSizeOptions: [
        { value: 10, text: "10" },
        { value: 25, text: "25" },
        { value: 50, text: "50" },
        { value: 100, text: "100" },
      ],
      filters: { status: "", bank: "", minAmount: "", maxAmount: "", search: "" },
    };
  },
  computed: {
    ...mapState(["userBanks", "userWithdraws", "profile", "userKyc"]),

    /* ---- Withdrawal limits, straight from /v1/kyc withdraw_limits ---- */
    withdrawLimits: function () {
      const k = this.userKyc || {};
      const w = k.withdraw_limits || {};
      // The payload nests a second `limits` object inside `withdraw_limits`.
      return w.limits || {};
    },
    withdrawUsage: function () {
      const w = (this.userKyc && this.userKyc.withdraw_limits) || {};
      return w.usage || {};
    },
    isKycVerified: function () {
      const k = this.userKyc || {};
      const author = (this.profile && this.profile.author) || {};
      return k.is_verified === true || k.is_kyc_verified === true || author.is_kyc_verified === true;
    },
    limitTier: function () {
      const w = (this.userKyc && this.userKyc.withdraw_limits) || {};
      const t = w.tier || this.withdrawLimits.tier;
      return t ? String(t).replace(/_/g, " ") : "";
    },
    hasLimits: function () {
      return Object.keys(this.withdrawLimits).length > 0;
    },
    minAmount: function () { return this.numOrNull(this.withdrawLimits.min_amount); },
    maxAmount: function () { return this.numOrNull(this.withdrawLimits.max_amount); },
    dailyCountLeft: function () {
      const total = this.numOrNull(this.withdrawLimits.daily_count);
      if (total === null) { return null; }
      return Math.max(0, total - (Number(this.withdrawUsage.daily_count) || 0));
    },
    dailyCountExhausted: function () {
      return this.dailyCountLeft === 0;
    },
    /** Only the limits the API actually supplied - no invented rows. */
    limitRows: function () {
      const L = this.withdrawLimits;
      const U = this.withdrawUsage;
      const rows = [];
      const money = this.money;

      if (this.minAmount !== null) {
        rows.push({ label: "Minimum per request", value: "$ " + money(this.minAmount) });
      }
      if (this.maxAmount !== null) {
        rows.push({ label: "Maximum per request", value: "$ " + money(this.maxAmount) });
      }
      if (this.numOrNull(L.daily_count) !== null) {
        rows.push({
          label: "Requests per day",
          value: this.dailyCountLeft + " of " + Number(L.daily_count) + " left",
          note: (Number(U.daily_count) || 0) + " used today",
        });
      }
      if (this.numOrNull(L.daily_amount) !== null) {
        const left = Math.max(0, Number(L.daily_amount) - (Number(U.daily_amount) || 0));
        rows.push({ label: "Daily amount", value: "$ " + money(left) + " left",
                    note: "of $ " + money(L.daily_amount) });
      }
      if (this.numOrNull(L.monthly_count) !== null) {
        const left = Math.max(0, Number(L.monthly_count) - (Number(U.monthly_count) || 0));
        rows.push({ label: "Requests this month", value: left + " of " + Number(L.monthly_count) + " left" });
      }
      if (this.numOrNull(L.monthly_amount) !== null) {
        const left = Math.max(0, Number(L.monthly_amount) - (Number(U.monthly_amount) || 0));
        rows.push({ label: "Monthly amount", value: "$ " + money(left) + " left",
                    note: "of $ " + money(L.monthly_amount) });
      }
      return rows;
    },
    myBanks: function () {
      return Array.isArray(this.userBanks) ? this.userBanks : [];
    },

    /* ---------------- history: sort -> filter -> page ---------------- */

    /** Newest first. The rows carry no date, so the id is the only ordering the API gives. */
    withdraws: function () {
      const rows = Array.isArray(this.userWithdraws) ? this.userWithdraws.slice() : [];
      return rows.sort(function (a, b) { return Number(b.id || 0) - Number(a.id || 0); });
    },
    /** Statuses that are actually present, plus any known one, so the select is never empty. */
    statusOptions: function () {
      const vm = this;
      const seen = {};
      this.withdraws.forEach(function (r) {
        const s = String(r.status || "").toLowerCase();
        if (s) { seen[s] = true; }
      });
      const ordered = KNOWN_STATUSES.filter(function (s) { return seen[s]; });
      Object.keys(seen).forEach(function (s) {
        if (ordered.indexOf(s) === -1) { ordered.push(s); }
      });
      return [{ value: "", text: "All statuses" }].concat(
        ordered.map(function (s) { return { value: s, text: vm.prettify(s) }; })
      );
    },
    bankFilterOptions: function () {
      const vm = this;
      const seen = [];
      this.withdraws.forEach(function (r) {
        if (r.bank !== undefined && r.bank !== null && seen.indexOf(String(r.bank)) === -1) {
          seen.push(String(r.bank));
        }
      });
      return [{ value: "", text: "All banks" }].concat(
        seen.map(function (id) { return { value: id, text: vm.bankLabel(id) }; })
      );
    },
    filteredWithdraws: function () {
      const vm = this;
      const f = this.filters;
      const term = String(f.search || "").trim().toLowerCase();
      const min = f.minAmount === "" ? null : Number(f.minAmount);
      const max = f.maxAmount === "" ? null : Number(f.maxAmount);

      return this.withdraws.filter(function (r) {
        if (f.status && String(r.status || "").toLowerCase() !== f.status) { return false; }
        if (f.bank && String(r.bank) !== String(f.bank)) { return false; }
        const amount = vm.num(r.amount);
        if (min !== null && !isNaN(min) && amount < min) { return false; }
        if (max !== null && !isNaN(max) && amount > max) { return false; }
        if (term) {
          const hay = [
            r.id, r.remarks, r.status, r.payout_currency,
            vm.bankLabel(r.bank),
          ].join(" ").toLowerCase();
          if (hay.indexOf(term) === -1) { return false; }
        }
        return true;
      });
    },
    totalItems: function () { return this.filteredWithdraws.length; },
    totalPages: function () { return Math.max(1, Math.ceil(this.totalItems / this.perPage)); },
    /**
     * Everything below pages off THIS, never off `currentPage` directly. Narrowing a
     * filter shrinks totalPages before the watcher has run, and for that one tick a raw
     * `currentPage` yields an empty table under a "Showing 51-47 of 47" caption.
     */
    safePage: function () { return Math.min(Math.max(1, this.currentPage), this.totalPages); },
    offset: function () { return (this.safePage - 1) * this.perPage; },
    pagedWithdraws: function () {
      return this.filteredWithdraws.slice(this.offset, this.offset + this.perPage);
    },
    firstIndex: function () { return this.totalItems ? this.offset + 1 : 0; },
    lastIndex: function () { return Math.min(this.offset + this.perPage, this.totalItems); },
    pageWindow: function () {
      const total = this.totalPages, cur = this.safePage;
      let start = Math.max(1, cur - 2);
      const end = Math.min(total, start + 4);
      start = Math.max(1, Math.min(start, end - 4));
      const out = [];
      for (let i = start; i <= end; i++) { out.push(i); }
      return out;
    },
    /** The payout column only earns its width when something is actually paid out. */
    anyPayout: function () {
      const vm = this;
      return this.withdraws.some(function (r) { return vm.hasPayout(r); });
    },
    fields: function () {
      const f = [
        { key: "index", label: "S.N.", class: "col-sn" },
        { key: "bank", label: "Bank", class: "col-bank" },
        { key: "amount", label: "Requested", class: "col-num text-right" },
        { key: "total_fee", label: "Charges", class: "col-num text-right" },
        { key: "net_amount", label: "You Receive", class: "col-num text-right" },
      ];
      if (this.anyPayout) {
        f.push({ key: "payout", label: "Payout", class: "col-num text-right" });
      }
      f.push({ key: "status", label: "Status", class: "col-status" });
      f.push({ key: "actions", label: "", class: "col-act text-right" });
      return f;
    },

    /* ---------------- detail + confirm breakdowns ---------------- */

    detailSections: function () {
      const d = this.detail || {};
      const sections = [];

      sections.push({
        title: "Withdrawal",
        items: [
          { label: "Reference", value: d.id ? "#" + d.id : "" },
          { label: "Bank account", value: this.bankLabel(d.bank) },
          { label: "Status", value: this.prettify(d.status) },
          { label: "Remarks", value: cleanText(d.remarks) },
        ],
      });

      const charges = [{ label: "Requested amount", value: "$ " + this.money(d.amount) }]
        .concat(this.chargeLines(d, "Currency conversion fee"));
      if (this.has(d.net_amount)) {
        charges.push({ label: "You receive", value: "$ " + this.money(d.net_amount), strong: true });
      }
      sections.push({ title: "Charges", items: charges });

      if (this.hasPayout(d) || d.payout_currency) {
        const payout = [{ label: "Payout currency", value: d.payout_currency }];
        if (this.has(d.exchange_rate)) {
          payout.push({ label: "Exchange rate", value: this.rate(d.exchange_rate) });
        }
        if (this.has(d.buy_rate)) {
          payout.push({ label: "Buy rate", value: this.rate(d.buy_rate) });
        }
        if (this.has(d.sell_rate)) {
          payout.push({ label: "Sell rate", value: this.rate(d.sell_rate) });
        }
        if (this.has(d.payout_amount)) {
          payout.push({
            label: "Payout amount",
            value: d.payout_currency + " " + this.plain(d.payout_amount),
            strong: true,
          });
        }
        sections.push({ title: "Payout", items: payout });
      }
      return sections;
    },

    /** Confirm-dialog breakdown, built only from fields the create response returned. */
    draftRows: function () {
      const d = this.draft || {};
      if (!this.has(d.amount)) { return []; }
      const rows = [{ label: "Requested amount", value: "$ " + this.money(d.amount) }]
        .concat(this.chargeLines(d, "Conversion fee"));
      if (this.has(d.net_amount)) {
        rows.push({ label: "You will receive", value: "$ " + this.money(d.net_amount), strong: true });
      }
      if (this.has(d.payout_amount) && d.payout_currency) {
        rows.push({
          label: "Paid out as",
          value: d.payout_currency + " " + this.plain(d.payout_amount),
          strong: true,
        });
      }
      // A single line is just the amount restated - the sentence below already says it.
      return rows.length > 1 ? rows : [];
    },

    /**
     * Kept in CENTS, like every other money value the API sends, so it can be compared
     * with withdraw_limits (also cents) without one side being silently 100x the other.
     * money() converts for display.
     */
    balanceCents: function () {
      const a = this.profile && this.profile.author;
      return (this.profile && this.profile.coins !== undefined)
        ? this.profile.coins
        : ((a && a.coins) || 0);
    },
    /** Primary account first, so the default selection is the user's main bank. */
    bankOptions: function () {
      const vm = this;
      const sorted = this.myBanks.slice().sort(function (a, b) {
        const ap = String(a.bank_type || "").toLowerCase() === "primary" ? 0 : 1;
        const bp = String(b.bank_type || "").toLowerCase() === "primary" ? 0 : 1;
        return ap - bp;
      });
      return [{ value: null, text: "Select a bank account" }].concat(
        sorted.map(function (b) {
          const name = (b.bank && b.bank.bank_name) || "Bank";
          const acct = vm.maskAccount(b.account_number);
          const type = String(b.bank_type || "").toLowerCase() === "primary" ? " (Primary)" : "";
          return { value: b.id, text: name + " - " + acct + type };
        })
      );
    },
  },
  watch: {
    // Filtering to 3 rows while sitting on page 7 would show an empty table.
    filters: {
      deep: true,
      handler: function () { this.currentPage = 1; },
    },
    perPage: function () { this.currentPage = 1; },
    totalPages: function (n) {
      if (this.currentPage > n) { this.currentPage = n; }
    },
  },
  created() {
    this.load();
  },
  beforeDestroy() {
    this.stopCooldown();
  },
  methods: {
    ...mapActions([
      "listMyBanks", "listUserWithdraws", "createUserWithdraw",
      "confirmUserWithdraw", "listProfile", "getUserKyc",
    ]),

    /** null when the API omitted a limit, so it is not rendered as "0". */
    numOrNull(v) {
      if (v === null || v === undefined || v === "") { return null; }
      const n = Number(v);
      return isNaN(n) ? null : n;
    },
    /** True only when the API sent a real number - 0 counts, null and "" do not. */
    has(v) {
      return this.numOrNull(v) !== null;
    },
    num(v) { const n = Number(v); return isNaN(n) ? 0 : n; },
    /**
     * Every plain money number from this API is in CENTS - amount, fee, conversion_fee,
     * total_fee, net_amount and the withdraw_limits values. `plain()` is for the few that
     * are not: payout_amount already arrives in major units of the payout currency.
     */
    money(v) { return centsToAmount(v).toFixed(2); },
    plain(v) { return this.num(v).toFixed(2); },
    /** "0.6500" -> "0.65%"; blank when the API sent nothing or a flat zero. */
    percent(v) {
      const n = this.numOrNull(v);
      if (n === null || n === 0) { return ""; }
      return String(parseFloat(n.toFixed(4))) + "%";
    },
    rate(v) {
      const n = this.numOrNull(v);
      return n === null ? "" : String(parseFloat(n.toFixed(6)));
    },
    /**
     * The charge lines for a row, in deduction order.
     *
     * Only fees that were ACTUALLY charged are listed: a withdrawal with no currency
     * conversion still returns conversion_fee: 0, and printing "- $ 0.00" against it
     * reads as a charge the customer did not pay. When nothing was deducted at all the
     * section says so in words rather than showing a row of zeroes.
     */
    chargeLines(row, conversionLabel) {
      const d = row || {};
      const lines = [];
      if (this.num(d.fee) > 0) {
        lines.push({ label: "Withdrawal fee", value: "- $ " + this.money(d.fee) });
      }
      if (this.num(d.conversion_fee) > 0) {
        const pct = this.percent(d.conversion_percent);
        lines.push({
          label: conversionLabel + (pct ? " (" + pct + ")" : ""),
          value: "- $ " + this.money(d.conversion_fee),
        });
      }
      // The total only adds information once there is more than one component to it.
      if (this.num(d.total_fee) > 0 && lines.length !== 1) {
        lines.push({ label: "Total charges", value: "- $ " + this.money(d.total_fee) });
      }
      if (!lines.length && this.has(d.total_fee)) {
        lines.push({ label: "Charges", value: "None" });
      }
      return lines;
    },
    hasPayout(row) {
      const r = row || {};
      return !!r.payout_currency && this.has(r.payout_amount);
    },
    load() {
      this.isLoading = true;
      const vm = this;
      return Promise.all([
        this.listMyBanks(), this.listUserWithdraws(), this.listProfile(),
        this.getUserKyc(),   // carries withdraw_limits
      ])
        .then(function () {
          vm.isLoading = false;
          // preselect the primary account
          if (!vm.form.bank && vm.bankOptions.length > 1) {
            vm.form.bank = vm.bankOptions[1].value;
          }
        })
        .catch(function () { vm.isLoading = false; });
    },
    goToPage(page) {
      const p = Math.min(Math.max(1, page), this.totalPages);
      this.currentPage = p;
    },
    resetFilters() {
      this.filters = { status: "", bank: "", minAmount: "", maxAmount: "", search: "" };
      this.currentPage = 1;
    },
    openDetail(row) {
      // The list already carries every field the detail shows, and the API exposes no
      // withdraws/{id}, so the popup opens instantly from the row instead of refetching.
      this.detail = row || {};
      this.$bvModal.show("wd-detail-modal");
    },
    maskAccount(n) {
      const s = String(n || "");
      return s.length <= 4 ? s : "•••• " + s.slice(-4);
    },
    bankLabel(bankId) {
      if (bankId === undefined || bankId === null || bankId === "") { return "-"; }
      const found = this.myBanks.filter(function (b) { return String(b.id) === String(bankId); });
      if (!found.length) { return "Bank #" + bankId; }
      const b = found[0];
      return ((b.bank && b.bank.bank_name) || "Bank") + " - " + this.maskAccount(b.account_number);
    },
    prettify(v) {
      if (!v) { return "-"; }
      return String(v).split("_").map(function (w) {
        return w.charAt(0).toUpperCase() + w.slice(1);
      }).join(" ");
    },
    statusClass(status) {
      const s = String(status || "").toLowerCase();
      if (s === "completed" || s === "success" || s === "accepted" || s === "approved") { return "is-success"; }
      if (s === "requested" || s === "on_process" || s === "processing" || s === "pending" || s === "initiated") { return "is-pending"; }
      return "is-failed";
    },
    validate() {
      const e = {};
      if (!this.form.bank) { e.bank = "Please choose a bank account."; }
      const amt = Number(this.form.amount);
      // The customer types dollars; the limits and the balance are cents.
      const cents = amountToCents(amt);

      // Checked against the SAME limits the sidebar displays, so the form can
      // never accept an amount the sidebar has just said is out of range.
      if (!String(this.form.amount).trim()) {
        e.amount = "Amount is required.";
      } else if (isNaN(amt) || amt <= 0) {
        e.amount = "Amount must be greater than 0.";
      } else if (this.minAmount !== null && cents < this.minAmount) {
        e.amount = "The minimum withdrawal is $ " + this.money(this.minAmount) + ".";
      } else if (this.maxAmount !== null && cents > this.maxAmount) {
        e.amount = "The maximum per request is $ " + this.money(this.maxAmount) + ".";
      } else if (cents > Number(this.balanceCents)) {
        e.amount = "Amount exceeds your available balance.";
      }

      if (this.dailyCountExhausted) {
        e.amount = "You have used all of today's withdrawal requests.";
      }
      this.errors = e;
      return Object.keys(e).length === 0;
    },
    startWithdraw() {
      this.formError = "";
      this.payError = "";
      if (!this.validate()) { return; }
      this.isSending = true;
      const vm = this;
      return this.createUserWithdraw({
        bank: this.form.bank,
        amount: amountToCents(this.form.amount),
        remarks: this.form.remarks || "",
      }).then(function (res) {
        if (res && res.ok) {
          vm.draft = res;
          vm.otp = "";
          vm.password = "";
          vm.errors = {};
          vm.$bvModal.show("wd-otp-modal");
          vm.startCooldown();
        } else {
          vm.formError = (res && res.insufficientBalance)
            ? "You do not have enough balance for this withdrawal."
            : ((res && res.message) || "Unable to start this withdrawal.");
        }
      }).then(function () { vm.isSending = false; })
        .catch(function () { vm.isSending = false; });
    },
    confirmWithdraw() {
      this.payError = "";
      const e = {};
      if (!this.otp) { e.otp = "Verification code is required."; }
      else if (this.otp.length !== 6) { e.otp = "Enter the 6-digit code."; }
      if (!this.password) { e.password = "Account password is required."; }
      this.errors = e;
      if (Object.keys(e).length) { return; }

      this.isConfirming = true;
      const vm = this;
      return this.confirmUserWithdraw({
        transaction_id: this.draft.transaction,
        otp: this.otp,
        password: this.password,
      }).then(function (res) {
        if (res && res.ok) {
          vm.infoText = res.message || "Withdrawal successful.";
          vm.password = "";
          vm.stopCooldown();
          vm.$bvModal.hide("wd-otp-modal");
          vm.$bvToast.show("wd-toast");
          vm.form = { bank: vm.form.bank, amount: "", remarks: "" };
          vm.currentPage = 1;
          return vm.load();
        }
        if (res && res.insufficientBalance) {
          vm.payError = "You do not have enough balance for this withdrawal.";
        } else if (res && res.passwordError) {
          vm.payError = res.message || "That password is not correct.";
          vm.$set(vm.errors, "password", vm.payError);
        } else if (res && res.otpError) {
          vm.payError = res.message || "That code is invalid or has expired. You can resend it.";
          vm.$set(vm.errors, "otp", vm.payError);
        } else {
          vm.payError = (res && res.message) || "Unable to complete this withdrawal.";
        }
      }).then(function () { vm.isConfirming = false; })
        .catch(function () {
          vm.isConfirming = false;
          vm.payError = "Unable to complete this withdrawal. Please try again.";
        });
    },
    resendCode() {
      if (this.resendCooldown > 0) { return; }
      const vm = this;
      this.payError = "";
      return this.createUserWithdraw({
        bank: this.form.bank,
        amount: amountToCents(this.form.amount),
        remarks: this.form.remarks || "",
      }).then(function (res) {
        if (res && res.ok) {
          vm.draft = res;
          vm.otp = "";
          vm.infoText = "A new verification code has been sent to your email.";
          vm.$bvToast.show("wd-toast");
        } else {
          vm.payError = (res && res.message) || "Unable to resend the code.";
        }
        vm.startCooldown();
      });
    },
    startCooldown() {
      this.stopCooldown();
      this.resendCooldown = 60;
      const vm = this;
      this.cooldownTimer = setInterval(function () {
        vm.resendCooldown = vm.resendCooldown - 1;
        if (vm.resendCooldown <= 0) { vm.stopCooldown(); }
      }, 1000);
    },
    stopCooldown() {
      if (this.cooldownTimer) { clearInterval(this.cooldownTimer); this.cooldownTimer = null; }
      this.resendCooldown = 0;
    },
  },
};
</script>

<style scoped>
/* ---- sidebar ---- */
.wd-side { margin-top: 0; }
@media (min-width: 992px) {
  .wd-side { position: sticky; top: 90px; }
}
.wd-limits {
  background: #f7f9fc;
  border: 1px solid #e4e9f0;
  border-radius: 8px;
  padding: 14px 16px;
  margin-bottom: 16px;
}
.wd-limits-head {
  display: flex;
  align-items: center;
  font-size: 13px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 10px;
}
.wd-limits-head .material-icons { font-size: 17px; margin-right: 6px; color: #0066b3; }
.wd-tier {
  margin-left: 10px;
  background: #eef4fb;
  color: #0066b3;
  border-radius: 10px;
  padding: 1px 9px;
  font-size: 11px;
  text-transform: capitalize;
}
/* One column in the sidebar; two only while it is stacked full-width on tablets. */
.wd-limit-grid {
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 10px 18px;
}
@media (min-width: 576px) and (max-width: 991.98px) {
  .wd-limit-grid { grid-template-columns: 1fr 1fr; }
}
.wd-limit { display: flex; flex-direction: column; }
.wd-limit-label { font-size: 12px; color: #8a94a6; }
.wd-limit-value { font-size: 14px; font-weight: 600; color: #2c3e50; }
.wd-limit-note { font-size: 11px; color: #8a94a6; }
.wd-limit-warn {
  margin-top: 12px;
  background: #fdeaea;
  border: 1px solid #f5c2c7;
  border-radius: 6px;
  padding: 8px 10px;
  font-size: 12px;
  color: #b02a37;
}
.wd-verify {
  display: inline-flex;
  align-items: center;
  margin-top: 12px;
  font-size: 13px;
  font-weight: 600;
}
.wd-verify .material-icons { font-size: 16px; margin-left: 4px; }

.wd-info {
  background: #fff;
  border: 1px solid rgba(0,0,0,0.08);
  border-radius: 8px;
  padding: 14px 16px;
}
.wd-info-head {
  display: flex; align-items: center;
  font-size: 13px; font-weight: 600; color: #2c3e50; margin-bottom: 8px;
}
.wd-info-head .material-icons { font-size: 17px; margin-right: 6px; color: #0066b3; }
.wd-info-list { margin: 0 0 8px 0; padding-left: 18px; }
.wd-info-list li { font-size: 12px; color: #6b7280; margin-bottom: 4px; }
.wd-info-foot { font-size: 11px; color: #8a94a6; margin: 0; }
</style>
<style lang="scss" scoped>
@import '../../css/style.scss';

.wd-card {
  background: #fff; border: 1px solid rgba(0,0,0,0.08); border-radius: 10px;
  padding: 26px; margin-bottom: 10px;
}
.balance-line {
  display: flex; justify-content: space-between; align-items: center;
  background: #f6f8fa; border-radius: 8px; padding: 12px 14px; margin-bottom: 18px;
}
.balance-line span { color: #6c757d; font-size: 14px; }
.balance-line strong { font-size: 22px; color: #0066b3; }
.empty-box {
  background: #f7f9fa; border: 1px dashed #dfe3e8; border-radius: 8px;
  padding: 14px; color: #6c757d; font-size: 13px;
}
.form-row-x { margin-bottom: 16px; }
.form-row-x label { display: block; font-size: 14px; color: #6c757d; margin-bottom: 6px; }
.text-danger { color: #dc3545; font-size: 12px; display: block; margin-top: 4px; }
.muted { color: #6c757d; font-size: 13px; }
.note { font-size: 13px; color: #6c757d; }
.link-muted { color: #0066b3; font-weight: 600; font-size: 14px; }
.list-title { font-size: 16px; font-weight: 600; margin-bottom: 12px; }
.wd-hint { font-size: 12px; color: #8a94a6; margin: 12px 0 0; }

/* ---- filters ---- */
.wd-filters label { font-size: 12px; color: #6b7280; margin-bottom: 4px; display: block; }
.wd-count { font-size: 12px; color: #6b7280; margin-bottom: 6px; }

/* ---- table ---- */
.wd-table { font-size: 14px; }
.wd-table .col-sn { width: 56px; }
.wd-table .col-num { white-space: nowrap; font-variant-numeric: tabular-nums; }
.wd-table .col-status { width: 116px; }
.wd-table .col-act { width: 70px; }
.wd-bank { display: block; }
.wd-ref { display: block; color: #8a94a6; font-size: 11px; }
.wd-view { color: #0066b3; font-weight: 600; font-size: 13px; white-space: nowrap; }
.debit { color: #dc3545; }

.status-pill { font-size: 11px; padding: 2px 9px; border-radius: 10px; font-weight: 600; white-space: nowrap; }
.status-pill.is-success { background: #e3f7e3; color: #00a800; }
.status-pill.is-pending { background: #fdf3e0; color: #e08b00; }
.status-pill.is-failed { background: #fde3e5; color: #dc3545; }

/* ---- pager (same pattern as the statement pages) ---- */
.st-pager { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; margin-top: 6px; }
.st-pagesize { display: flex; align-items: center; font-size: 13px; color: #6b7280; }
.st-pagesize span { margin-right: 8px; white-space: nowrap; }
.st-pagesize select { width: auto !important; min-width: 76px; }
.st-pageinfo { font-size: 13px; color: #6b7280; }

/* ---- confirm breakdown ---- */
.wd-breakdown {
  background: #f7f9fc; border: 1px solid #e4e9f0; border-radius: 8px;
  padding: 12px 14px; margin-bottom: 14px;
}
.wd-breakdown-row {
  display: flex; justify-content: space-between; font-size: 13px;
  color: #6b7280; padding: 4px 0;
}
.wd-breakdown-row.is-strong {
  color: #2c3e50; font-weight: 700; font-size: 15px;
  border-top: 1px solid #e4e9f0; margin-top: 4px; padding-top: 8px;
}

/* ---- detail modal ---- */
.txn-head { border: 1px solid #e9ecef; background: #f8f9fb; border-radius: 8px; padding: 16px 18px; margin-bottom: 18px; }
.txn-amount { font-size: 26px; font-weight: 700; line-height: 1.2; display: inline-block; margin-right: 10px; color: #0066b3; }
.txn-sub { color: #6b7280; font-size: 13px; margin-top: 6px; }
.txn-section { margin-bottom: 16px; }
.txn-section-title { font-size: 12px; text-transform: uppercase; letter-spacing: .4px; color: #8a94a6; margin-bottom: 8px; }
.txn-row { display: flex; justify-content: space-between; padding: 6px 0; border-bottom: 1px solid #f2f4f7; font-size: 14px; }
.txn-row:last-child { border-bottom: 0; }
.txn-row.is-strong { font-weight: 700; }
.txn-key { color: #6b7280; margin-right: 16px; }
.txn-val { font-weight: 500; text-align: right; word-break: break-word; }
</style>
